import React, { memo, useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { usePrevious } from '@atc/use-previous';

import { sendClick, sendImpressions } from 'reaxl-analytics';
import { buildAndPriceClick, ownerEmailClick, preorderInventoryImpressions, snapshotPaymentDetailsClick, telMetricsPhoneTextClick } from 'reaxl-analytics-handlers';
import { InventoryListingPlaceholder } from 'reaxl-listing';

import getPreorderAvailability, { isSingleYear } from '@/utilities/getPreorderAvailability';
import getPreorderYear from '@/utilities/getPreorderYear';
import getUniqueMakeModelPairs from '@/utilities/getUniqueMakeModelPairs';
import { getAbsolutePathWithListingId } from '@/utilities/vdpUrlBuilder';

// ducks
import bapActiveModelDuck from '@/ducks/bap/bapActiveModelDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import kbbVRSDataDuck from '@/ducks/kbbVRSDataDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import preorderDuck from '@/ducks/preorderDuck.js';
import srpActiveEmailListingDuck from '@/ducks/srp/srpActiveEmailListingDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';

import useBapNavigation from '@/hooks/useBapNavigation';

// utilities

import PreorderCard from '@/components/PreorderCard/index.js';

function PreorderSearchContainer() {
    const device = useDevice();
    const notXS = _get(device, 'greaterThan.xs', false);
    const navigateToBap = useBapNavigation();

    const dispatch = useDispatch();

    const vrsData = useSelector(kbbVRSDataDuck.selectors.getData);
    const isVrsDataLoading = useSelector(kbbVRSDataDuck.selectors.getLoading);
    const prevVrsData = useRef({});
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const filtersRequestValues = useSelector(srpFiltersDuck.selectors.getRequestValues);
    const prevFiltersRequestValues = useRef({});
    const preorderListings = useSelector(preorderDuck.selectors.getPreorder);
    const prevPreorderListings = usePrevious(preorderListings, []);
    const birf = useSelector(birfDuck.selectors.getPageBirf);
    const isLoading = useSelector(preorderDuck.selectors.isLoading);
    const showListingWalletPayment = useSelector((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));

    const {
        market_extension: [marketExtensionEnabled],
        my_wallet: [enableMyWallet],
        preorder: [enablePreorder],
        retail_engine: [enableRetailEngine],
        SRP_DEALERNAME_CLICKABLE: [isDealerNameClickable],
    } = useFeatures([
        'market_extension',
        'my_wallet',
        'preorder',
        'retail_engine',
        'SRP_DEALERNAME_CLICKABLE',
    ]);

    const makeCodeList = filtersRequestValues?.makeCodeList || filtersRequestValues?.makeCode;
    const modelCodeList = filtersRequestValues?.modelCodeList || filtersRequestValues?.modelCode;
    const zip = filtersRequestValues?.zip;

    const isEligibleForPreorder = enableRetailEngine && enablePreorder && getPreorderAvailability(filtersRequestValues);
    const showPreorderCard = enableRetailEngine && enablePreorder && preorderListings.length > 0
        && getPreorderAvailability(filtersRequestValues);

    const handleRequestAQuoteClick = useCallback((listing = {}) => async (event) => {
        dispatch(srpActiveEmailListingDuck.creators.setActiveResults([listing?.id]));
        dispatch(srpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: 'quote',
            emailSubject: 'New Car Custom Order Inquiry',
            ownerId: '',
            showEmailModal: true,
        }));
        sendClick(ownerEmailClick, event, {
            par: 'newcarorder',
            includePageId: true,
            inventoryId: listing.id,
            ownerId: listing.owner.id,
            context: 'quote',
        });
    }, [dispatch]);

    const handleBuildClick = useCallback((event, listing = {}) => {
        dispatch(bapActiveModelDuck.creators.setActiveModel(listing.modelCode || ''));
        navigateToBap({
            makeCode: listing.makeCode,
            modelCode: listing.modelCode,
            ownerId: listing.owner?.id,
            year: listing.year,
            zip,
        });
        sendClick(buildAndPriceClick, event, {
            par: 'newcarorder',
            inventoryId: listing?.id,
            ownerId: listing?.owner?.id,
        });
    }, [dispatch, navigateToBap, zip]);

    const handleNewCarWalletClick = useCallback((listing = {}) => async (event) => {
        event.stopPropagation();

        const extra = {
            inventoryId: listing?.id,
            par: 'preorder',
        };
        sendClick(snapshotPaymentDetailsClick, event, extra);

        dispatch(paymentsDuck.creators.updateMyWallet({
            listingId: listing.id,
            showPaymentDetailsModal: true,
        }));
    }, [dispatch]);

    // TODO click type should be preorder?
    const handlePhoneNumberClick = useCallback((listing, phoneNumber, clickType) => {
        const data = {
            birf,
            clickType,
            ownerId: listing.owner.id,
            listing: {
                ...listing,
                id: null,
                vdpURL: getAbsolutePathWithListingId(window?.location?.origin, listing?.id),
            },
            phoneNumber,
        };
        sendClick(telMetricsPhoneTextClick, {}, data);
    }, [birf]);

    const getListingProps = (listing) => {
        const ownerWebsite = isDealerNameClickable ? _get(listing, 'owner.website', {}) : null;
        const isDealerDeliveryAvailable = marketExtensionEnabled ? _get(listing, 'owner.shippingLabel', '') : null;

        return {
            className: 'cursor-pointer flex-column',
            disableRetailEngine: !enableRetailEngine,
            isPhoneNumberCallable: !notXS,
            showListingPaymentRibbon: !!enableMyWallet && !!showListingWalletPayment,
            onMyWalletClick: handleNewCarWalletClick(listing),
            phoneClickHandler: (phoneNumber) => handlePhoneNumberClick(listing, phoneNumber, 'buildPrice'),
            listing: {
                ...listing,
                owner: {
                    ...listing?.owner,
                    shippingLabel: isDealerDeliveryAvailable,
                    website: ownerWebsite,
                },
            },
            id: null,
            orientation: 'portrait',
            uiContext: 'preorder',
            emailOwnerProps: {
                onCTAClick: handleRequestAQuoteClick(listing),
                onBuildClick: !_isEmpty(vrsData) ? (event) => handleBuildClick(event, listing) : null,
            },
        };
    };

    useEffect(() => {
        if (!_isEqual(preorderListings, prevPreorderListings)) {
            preorderListings.forEach((listing) => {
                sendImpressions({
                    data: {
                        inventoryId: listing?.id,
                        ownerId: listing?.owner?.id,
                    },
                    name: preorderInventoryImpressions,
                });
            });
        }

    }, [preorderListings, prevPreorderListings]);

    // fetch up to 3 make model pairs of vrs data - multi-year search requests one model for each year and
    // returns latest year available first
    useEffect(() => {
        const isSingleYearSearch = isSingleYear(filtersValues);

        if (makeCodeList && modelCodeList && enablePreorder && enableRetailEngine) {
            const makeModelPairs = getUniqueMakeModelPairs({ filtersValues, multiYear: !isSingleYearSearch }).slice(0, 6);

            dispatch(kbbVRSDataDuck.creators.loadData({ makeModelPairs }));
        }
    }, [dispatch, makeCodeList, modelCodeList, enablePreorder, enableRetailEngine, filtersValues]);

    // update preorder listings after VRS data is populated
    useEffect(() => {
        // Don't load preorder listings if VRS data is still loading
        if (enableRetailEngine && enablePreorder && !_isEmpty(vrsData) && getPreorderAvailability(filtersRequestValues) && (!_isEqual(vrsData, prevVrsData.current) || !_isEqual(filtersRequestValues, prevFiltersRequestValues.current))) {
            prevVrsData.current = vrsData;
            prevFiltersRequestValues.current = filtersRequestValues;

            const isSingleYearSearch = isSingleYear(filtersValues);
            const startYear = Number(filtersRequestValues?.startYear);
            const endYear = Number(filtersRequestValues?.endYear);
            const year = getPreorderYear({ endYear, startYear });
            const makeModelPairs = getUniqueMakeModelPairs({ filtersValues, multiYear: !isSingleYearSearch });
            const options = { vrsData, makeModelPairs, makeCodeList, modelCodeList, year, zip };

            dispatch(preorderDuck.creators.clearPreorderListings());
            dispatch(preorderDuck.creators.loadData(options));
        } else if (!isVrsDataLoading && _isEmpty(vrsData)) {
            // if no VRS data is available, set loading to false because we aren't going to do it at the end of
            // preorderDuck.creators.loadData() like we do when we are actually trying to load preorder data
            dispatch(preorderDuck.creators.setKeys({ loading: false }));
        }

    }, [dispatch, enablePreorder, enableRetailEngine, filtersRequestValues, filtersValues, isVrsDataLoading, makeCodeList, modelCodeList, vrsData, zip]);

    // reset Build & Price active model
    useEffect(() => {
        dispatch(bapActiveModelDuck.creators.setActiveModel(''));
    }, [dispatch]);

    const renderPreorderCards = showPreorderCard && (
        preorderListings.map(((listing) => (
            <div
                className="col-xs-12 col-sm-4 display-flex fade-in"
                key={`preorder-${listing.id}`}
            >
                <PreorderCard
                    {...getListingProps(listing)}
                />
            </div>
        )))
    );

    // Show placeholder while VRS data is still loading or while preorder data is loading since VRS has to load first.
    return isEligibleForPreorder && (isLoading || isVrsDataLoading ? (
        <InventoryListingPlaceholder
            className="col-xs-12 col-sm-4"
            data-cmp="inv-placeholder-preorder"
            key="preorder-inventory-placeholder"
        />
    ) : renderPreorderCards);
}

export default memo(PreorderSearchContainer);
